import { Link } from "react-router-dom";
import { ArrowLeft, Mic, Printer } from "lucide-react";
import { Brand } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Nice } from "@/components/Nice";

const TIPS = [
  ["Model it out loud", "Say “please” and “thank you” to your own voice assistants and apps. Kids copy what they hear far more than what they're told."],
  ["Treat AI like a helper, not a servant", "Explain that polite words are about who we are, not who we're talking to. Manners are a habit that travels everywhere."],
  ["Celebrate, don't correct", "When your child remembers the magic words, notice it warmly. A quick “That was so kind” beats a lecture every time."],
  ["Use gentle reminders", "Try “What's the magic word?” or a playful pause instead of saying no. Give them the chance to fix it themselves."],
  ["Make thank-you a finish line", "Help kids see that a task isn't done until they've said thanks — at dinner, after help with homework, or with AI."],
  ["Ask clearly, together", "Practice turning “Do my homework” into “Would you please help me understand this problem?” Clear, kind asks get better answers."],
  ["Talk about what AI heard", "Ask your child: “Did it understand what you meant?” This builds listening skills and teaches them to check before acting."],
  ["Grow gratitude language", "Please and thank you are the start. Try gratitude words together: “I would be so grateful if…”, “I really appreciate…”, “Thank you so much, that really helped!” NICE rewards these with golden acorns."],
  ["Teach the 3 steps of an apology", "1) State the problem: “You probably feel ___, because I ___.” 2) Make the apology: “I'm sorry.” 3) Do the repair work, or ask “How can I make it better?” It works for parents, too. Try the 3 Steps of an Apology in NICE together."],
  ["Keep it short and regular", "A few polite requests a day is plenty. Consistency matters more than long sessions."],
];

export default function ParentTips() {
  return (
    <div className="pp-app mode-kid" data-testid="parent-tips-page">
      <header className="pp-header">
        <Brand />
        <div className="flex gap-2 flex-wrap no-print">
          <Link to="/voice" className="pp-ghost" data-testid="voice-setup-link">
            <Mic className="h-4 w-4" /> Siri & Alexa
          </Link>
          <Link to="/cards" className="pp-ghost" data-testid="tips-card-library-link">Card library</Link>
          <button className="pp-ghost" data-testid="print-tips-button" onClick={() => window.print()}>
            <Printer className="h-4 w-4" /> Print tips
          </button>
          <Link to="/" className="pp-ghost" data-testid="back-to-app-link">
            <ArrowLeft className="h-4 w-4" /> Back to <Nice />
          </Link>
        </div>
      </header>
      <main className="pp-main">
        <p className="pp-label">For parents & caregivers</p>
        <h1 className="pp-h1">Raising kind kids in an AI world</h1>
        <p className="pp-sub max-w-xl">
          Simple, everyday ways to encourage polite language at home — with people and with technology.
        </p>
        <Link to="/print/apology-cards" target="_blank" className="pp-ghost mt-6 no-print" data-testid="tips-print-apology-cards">
          <Printer className="h-4 w-4" /> Print the apology cards
        </Link>
        <ol className="pp-tips" data-testid="parent-tips-list">
          {TIPS.map(([title, body], i) => (
            <li key={title} className="pp-tip" style={{ animationDelay: `${i * 60}ms` }} data-testid={`parent-tip-${i}`}>
              <span className="pp-tip-num">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h2 className="pp-h2">{title}</h2>
                <p className="mt-2 text-[15px] leading-relaxed opacity-80">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </main>
      <Footer />
    </div>
  );
}
